import { useCallback, useState } from "react";
import { FileRejection } from "react-dropzone";
import { usePdfValidation } from "./usePdfValidation";
import { usePngValidation } from "./usePngValidation";

type TFileType = "pdf" | "png" | "unknown";
export type TFileValidationError = {
    error: "file-invalid-type" | "file-too-large" | "certificate-id-missing" | "unknown";
    fileType: TFileType;
};

const getFileType = (file: File): TFileType => {
    switch (file.type) {
        case "application/pdf":
            return "pdf";
        case "image/png":
            return "png";
        default:
            return "unknown";
    }
};

export const useFileValidation = (
    environment: TEnv,
    api_identifier: string,
    brandName: TAccountType
) => {
    const [error, setError] = useState<TFileValidationError | null>(null);

    const { validatePdf, loading: pdfLoading } = usePdfValidation(
        environment,
        api_identifier,
        brandName
    );
    const { validatePng, loading: pngLoading } = usePngValidation(environment, api_identifier, brandName);

    const handleFileDrop = useCallback(
        async (acceptedFiles: File[], fileRejections: FileRejection[]) => {
            setError(null);

            let file: File | null = null;

            if (acceptedFiles.length > 0) {
                file = acceptedFiles[0];
            }

            if (fileRejections.length > 0) {
                const rejection = fileRejections[0];
                const errors = rejection.errors;

                // Find the specific error type
                const isFileTooLarge = errors.some((error) => error.code === "file-too-large");
                const isFileTypeInvalid = errors.some((error) => error.code === "file-invalid-type");

                const fileType = getFileType(rejection.file);

                switch (true) {
                    case isFileTypeInvalid:
                        setError({ error: "file-invalid-type", fileType });
                        break;
                    case isFileTooLarge:
                        setError({ error: "file-too-large", fileType });
                        break;
                    default:
                        setError({ error: "unknown", fileType });
                        break;
                }
                return;
            }

            if (file?.type.endsWith("pdf")) {
                try {
                    const result = await validatePdf(file);
                    if (result && typeof result === "object" && "error" in result) {
                        setError({ error: result.error, fileType: getFileType(file) });
                        return;
                    }
                    return result;
                } catch (error) {
                    setError({ error: "unknown", fileType: getFileType(file) });
                }
            }

            if (file?.type === "image/png") {
                try {
                    const result = await validatePng(file);
                    if (result && typeof result === "object" && "error" in result) {
                        setError({ error: result.error, fileType: getFileType(file) });
                        return;
                    }
                    return result;
                } catch (error) {
                    setError({ error: "unknown", fileType: getFileType(file) });
                }
            }
        },
        [validatePdf, validatePng]
    );

    return {
        handleFileDrop,
        loading: pdfLoading || pngLoading,
        error,
    };
};
